/** @jsxImportSource @emotion/react */
import { Link, useNavigate } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import { Divider, FormControl, Grid, IconButton, MenuItem, Select, Typography } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CastIcon from '@mui/icons-material/Cast'
import TagIcon from '@mui/icons-material/Tag'
import CodeIcon from '@mui/icons-material/Code'
import { fullName, normalizeSuffix, physicianFullName } from 'helpers/nameHelper'
import BuildingIcon from '@mui/icons-material/CorporateFare'
import DateRangeIcon from '@mui/icons-material/DateRange'
import NoteIcon from '@mui/icons-material/StickyNote2'
import SettingsApplicationsIcon from '@mui/icons-material/Balcony'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import LocalHospital from '@mui/icons-material/LocalHospital'
import TollIcon from '@mui/icons-material/Toll'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import CallOutlinedIcon from '@mui/icons-material/CallOutlined'
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined'
import BedIcon from '@mui/icons-material/Bed'
import DevicesIcon from '@mui/icons-material/Devices'
import moment from 'moment'
import { useDebouncedCallback } from 'use-debounce'
import { useMemo, useState } from 'react'
import { ClickablePhone } from 'components/atoms/ClickablePhone'
import { VoiceOptOutBadge } from 'components/atoms/VoiceOptOutBadge'

import { TooltipWrapper } from 'components/atoms/TooltipWrapper'
import avatar from 'assets/img/avatar/user.svg'
import routes from 'components/lab/constants/routes'
import { Can } from 'components/lab/Can'
import { UserPermission } from 'components/lab/user/permissions'
import TestConditionWidget from 'components/dme/orderEdit/views/header/TestConditionWidget'
import { NEEDED_DEVICE_TYPES, NeededDeviceType, Order, PhysicianNote } from 'store/types'
import config from 'config/app'
import { confirm } from 'components/molecules/Confirmation'
import { DeviceComplaintModal } from 'components/organisms/deviceComplaints/DeviceComplaintModal'
import { useSetNeededDeviceTypeMutation } from 'store/services/labOrder'
import {
    useGetEffectiveFollowUpPhysiciansQuery,
    useGetEffectivePreferredDmeOfficeQuery,
    useGetLabPhysicianNotesQuery,
} from 'store/services/labPhysician'
import { useGetLabOfficeQuery } from 'store/services/office'
import { useGetPatientBalancesQuery } from 'store/services/patientTransactions'
import { isOrganization } from '../../../../helpers/followUpProvidersHelper'

const testName = {
  HST: 'Home Sleep Test',
  PSG: 'In-Lab Sleep Test',
  POX: 'Pulse Oximetry',
  COX: 'Capnography',
}
const deviceTypes = {
  NIGHT_OWL: 'NightOwl',
  DREAM_CLEAR: 'Dreamclear',
  HST: 'Home Sleep Device',
}
// Balances are account-level, so an amount that hasn't loaded yet reads as "-" rather
// than $0.00 — a real zero balance and an unanswered request mean different things here.
const money = (amount?: number | null) =>
  amount == null ? '-' : `$${Number(amount).toFixed(2)}`

interface IPatientInfo {
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zip: string
  email?: string
  homePhone?: string
  smartPhone?: string
  voiceOptOut?: boolean
  id: number
  name: string
  testTitle: string
  profileImageUrl?: string
  dateOfBirth?: string

  order: Order

  testTypeMatch?: boolean
  rxTestType?: string
}

interface IContactInfo {
  patientId?: number
  testType: string
  coPayAmount: string
  stripeCustomerId: string
  hasCardOnFile?: boolean
  effectiveDate: string
  terminationDate: string
  authValid: string
  units: string
  payor: string
  labBuckets: string
}

interface IProviderInfo {
  officeName: string
  officeId: number
  companyName?: string
  physicianName: string
  physicianNpi?: string
  testType: string
  interpretingPhysician?: string
  followUpProviders: {
    id: string
    label: string
    preferredDmeOfficeLabel?: string | null
  }[]
  followUpCareNoneExplicitlySet: boolean
  effectivePreferredDmeOfficeLabel?: string | null
  preferredDmeNoneExplicitlySet: boolean
  physicianNotes: PhysicianNote[]
  clientNote?: string | null
  order: Order
}

interface ILocationInfo {
  order: Order
  deviceKind?: string
  serialNumber?: string
  deviceBrand?: string
  coPayAmount: string
  stripeCustomerId: string
  createMobileInvite: () => void
  addNight: (night: number) => void
  notes?: string
}

interface PatientHeaderTwoProps {
  order: Order
  onChangeDeviceClick: () => void
  onEditTestConditionsClick: () => void
  createMobileInvite: (params: any) => void
  addNight: (night: number) => void
  companyName?: string
}

const PinnedNoteDisplay = ({ pinnedNote }: { pinnedNote: string }) => (
  <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start">
    <Typography
      sx={{
        display: 'flex',
        alignItems: 'center',
        columnGap: '5px',
        font: '600 12px/14px Source Sans Pro',
        color: '#6D757E',
      }}>
      <NoteIcon htmlColor="#6D757E" />
      Note:
    </Typography>
    <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
      {pinnedNote}
    </Typography>
  </Grid>
)

const PatientInfo = ({
  addressLine1,
  addressLine2,
  city,
  state,
  zip,
  email,
  homePhone,
  smartPhone,
  voiceOptOut,
  id,
  name,
  testTitle,
  order,
  dateOfBirth,
  testTypeMatch,
  rxTestType,
}: IPatientInfo & { onChangeDeviceClick: () => void }) => {
  const navigate = useNavigate()
  const availableTests = order?.office?.availableTests

  const onConvertToInlab = useDebouncedCallback(() => {
    confirm('', {
      title: 'Are you sure?',
      description:
        'This will cancel the current order and generate a new In-Lab order using the data from the current one.',
      yesAction: 'Continue',
      noAction: 'Cancel',
    })
      .then(() => {
        navigate(
          `${routes.index}${routes.order.root}/new/patient/${order?.patientId}/orderDetails?officeId=${order?.officeId}&testType=PSG&followUpOrder=true&moveOrderId=${order.id}`,
        )
      })
      .catch(() => {})
  }, 300)

  const onConvertToHst = useDebouncedCallback(() => {
    confirm('', {
      title: 'Are you sure?',
      description:
        'This will cancel the current order and generate a new HST order using the data from the existing one.',
      yesAction: 'Continue',
      noAction: 'Cancel',
    })
      .then(() => {
        navigate(
          `${routes.index}${routes.order.root}/new/patient/${order?.patientId}/orderDetails?officeId=${order?.officeId}&testType=HST&followUpOrder=true&moveOrderId=${order.id}`,
        )
      })
      .catch(() => {})
  }, 300)

  return (
    <Grid
      container
      direction="column"
      sx={{ background: 'white', padding: '5px 10px', position: 'relative' }}
      rowGap="5px">
      <Link to={`${routes.index}${routes.patient.root}/${id}`}>
        <IconButton sx={{ position: 'absolute', top: '10px', right: '10px', padding: '2px' }}>
          <OpenInNewOutlinedIcon htmlColor="#000000" />
        </IconButton>
      </Link>
      <Grid item container flexWrap="nowrap" columnGap="8px" alignItems="center">
        <img src={avatar} alt="avatar" css={{ width: '25px', height: '25px' }} />
        <Grid container direction="row" alignItems="center">
          <Typography sx={{ font: '600 12px/14px Source Sans Pro', marginRight: '12px' }}>
            {name}
          </Typography>
          <TooltipWrapper text={rxTestType || ''} show={!testTypeMatch}>
            <Typography
              sx={{
                font: '600 13px/16px Source Sans Pro',
                color: testTypeMatch ? '#BAC1C9' : '#FF110088',
              }}>
              {testTitle}{' '}
              {order?.bucket !== 'COMPLETED' &&
                availableTests?.HST &&
                availableTests?.PSG &&
                (order?.testType === 'HST' ? (
                  <Button
                    disabled={!!order?.movedTo}
                    onClick={onConvertToInlab}
                    css={{ padding: '0 10px', height: 25, marginLeft: 10 }}>
                    Convert to In-Lab
                  </Button>
                ) : order?.testType === 'PSG' ? (
                  <Button
                    disabled={!!order?.movedTo}
                    onClick={onConvertToHst}
                    css={{ padding: '0 10px', height: 25, marginLeft: 10 }}>
                    Convert to HST
                  </Button>
                ) : (
                  <></>
                ))}
            </Typography>
          </TooltipWrapper>
        </Grid>
      </Grid>
      <Grid item container flexWrap="nowrap" justifyContent="space-between">
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: '5px',
            font: '600 12px/14px Source Sans Pro',
            color: '#6D757E',
          }}>
          <CalendarMonthIcon htmlColor="#6D757E" />
          Date of Birth:
        </Typography>
        <Typography sx={{ font: '600 12px/14px Source Sans Pro' }}>
          {moment(dateOfBirth).format('MM/DD/YYYY')} ({moment().diff(dateOfBirth, 'years')})
        </Typography>
      </Grid>
      <Divider />
      <Grid
        container
        direction="column"
        justifyContent="space-between"
        paddingBottom={'10px'}
        rowGap="5px">
        <Grid container wrap="nowrap" justifyContent="space-between">
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '5px',
              font: '600 12px/14px Source Sans Pro',
              color: '#6D757E',
            }}>
            <EmailOutlinedIcon htmlColor="#6D757E" />
            Email:
          </Typography>
          <Typography sx={{ font: '600 12px/14px Source Sans Pro' }}>{email}</Typography>
        </Grid>
        <Grid container wrap="nowrap" justifyContent="space-between">
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '5px',
              font: '600 12px/14px Source Sans Pro',
              color: '#6D757E',
            }}>
            <CallOutlinedIcon htmlColor="#6D757E" /> Home Phone:
          </Typography>
          <Typography sx={{ font: '600 12px/14px Source Sans Pro', display: 'flex', alignItems: 'center' }}>
            <ClickablePhone
              phoneNumber={homePhone}
              dateTo={order?.completedAt ? new Date(order.completedAt).toISOString() : order?.cancelledAt ? new Date(order.cancelledAt).toISOString() : undefined}
            />
            <VoiceOptOutBadge optedOut={voiceOptOut} />
          </Typography>
        </Grid>
        <Grid container wrap="nowrap" justifyContent="space-between">
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '5px',
              font: '600 12px/14px Source Sans Pro',
              color: '#6D757E',
            }}>
            <SmartphoneOutlinedIcon htmlColor="#6D757E" />
            Smart Phone:
          </Typography>
          <Typography sx={{ font: '600 12px/14px Source Sans Pro', display: 'flex', alignItems: 'center' }}>
            <ClickablePhone
              phoneNumber={smartPhone}
              dateTo={order?.completedAt ? new Date(order.completedAt).toISOString() : order?.cancelledAt ? new Date(order.cancelledAt).toISOString() : undefined}
            />
            <VoiceOptOutBadge optedOut={voiceOptOut} />
          </Typography>
        </Grid>
        <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start">
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '5px',
              font: '600 12px/14px Source Sans Pro',
              color: '#6D757E',
            }}>
            <LocationOnOutlinedIcon htmlColor="#6D757E" />
            Address:
          </Typography>
          <div>
            <Typography
              component="p"
              sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
              {addressLine1}
            </Typography>
            <Typography
              component="p"
              sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
              {addressLine2}
            </Typography>
            <Typography
              component="p"
              sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
              {city}, {state} {zip}
            </Typography>
          </div>
        </Grid>
      </Grid>
    </Grid>
  )
}

/**
 * Account-level balances for the order's patient — the same figures as the patient
 * account header, shown here so staff don't have to leave the order to see them.
 * "Current" marks them as live account totals, not anything scoped to this order.
 */
const PatientBalances = ({ patientId }: { patientId?: number }) => {
  const { data: balances } = useGetPatientBalancesQuery(
    { patientId: Number(patientId) },
    { skip: !patientId },
  )

  return (
    <>
      <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start">
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: '5px',
            font: '600 12px/14px Source Sans Pro',
            color: '#6D757E',
          }}>
          <TollIcon htmlColor="#6D757E" />
          Current Patient Balance:
        </Typography>
        <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
          {money(balances?.patientBalance)}
        </Typography>
      </Grid>
      <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start">
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: '5px',
            font: '600 12px/14px Source Sans Pro',
            color: '#6D757E',
          }}>
          <TollIcon htmlColor="#6D757E" />
          Current Unapplied Payments:
        </Typography>
        <Typography
          component="p"
          sx={{
            font: '600 12px/14px Source Sans Pro',
            textAlign: 'end',
            // Same green-when-credit-is-available cue as the patient account header.
            color: Number(balances?.unappliedPayment || 0) > 0 ? '#28a745' : undefined,
          }}>
          {money(balances?.unappliedPayment)}
        </Typography>
      </Grid>
    </>
  )
}

const ContactInfo = ({
  patientId,
  testType,
  coPayAmount,
  stripeCustomerId,
  hasCardOnFile,
  effectiveDate,
  terminationDate,
  authValid,
  units,
  payor,
  labBuckets,
}: IContactInfo) => (
  <Grid
    container
    direction="column"
    sx={{ background: 'white', padding: '10px', position: 'relative' }}
    justifyContent="flex-start">
    <Typography sx={{ font: '600 12px/14px Source Sans Pro' }}>{/*Contact Info:*/}</Typography>
    <Grid container wrap="nowrap" justifyContent="space-between" alignItems="center">
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '5px',
          font: '600 12px/14px Source Sans Pro',
          color: '#6D757E',
        }}>
        <BuildingIcon htmlColor="#6D757E" />
        Payor:
      </Typography>
      <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
        {payor}
      </Typography>
    </Grid>
    <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start">
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '5px',
          font: '600 12px/14px Source Sans Pro',
          color: '#6D757E',
        }}>
        <DateRangeIcon htmlColor="#6D757E" />
        Effective Dates:
      </Typography>
      <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
        {effectiveDate} - {terminationDate}
      </Typography>
    </Grid>
    <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start">
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '5px',
          font: '600 12px/14px Source Sans Pro',
          color: '#6D757E',
        }}>
        <DateRangeIcon htmlColor="#6D757E" />
        Authorization Valid:
      </Typography>
      <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
        {authValid}
      </Typography>
    </Grid>
    <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start">
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '5px',
          font: '600 12px/14px Source Sans Pro',
          color: '#6D757E',
        }}>
        <TollIcon htmlColor="#6D757E" />
        Units:
      </Typography>
      <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
        {units}
      </Typography>
    </Grid>
    {testType === 'PSG' && (
      <Grid
        padding="5px"
        container
        wrap="nowrap"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ background: '#F5F5F5' }}>
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: '5px',
            font: '600 12px/14px Source Sans Pro',
            color: '#6D757E',
          }}>
          <TollIcon htmlColor="#6D757E" />
          Patient Responsibility amount:
        </Typography>
        <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
          {coPayAmount}
        </Typography>
      </Grid>
    )}
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      paddingBottom={'10px'}
      rowGap="5px">
      <Grid container wrap="nowrap" alignItems="flex-start">
        <Grid
          padding="5px"
          container
          wrap="nowrap"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ background: '#F5F5F5' }}>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '5px',
              font: '600 12px/14px Source Sans Pro',
              color: '#6D757E',
            }}>
            <TollIcon htmlColor="#6D757E" />
            Patient Responsibility amount:
          </Typography>
          <Typography
            component="p"
            sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
            {coPayAmount}
          </Typography>
        </Grid>
        {stripeCustomerId && (
          <Button
            variant="outline-primary"
            css={{ whiteSpace: 'nowrap', marginLeft: 5 }}
            href={`${config?.stripeCustomerUrl}${stripeCustomerId}`}
            target="_blank">
            Charge CC
          </Button>
        )}
      </Grid>
      <PatientBalances patientId={patientId} />
      {hasCardOnFile !== undefined && (
        <Grid container wrap="nowrap" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '5px',
              font: '600 12px/14px Source Sans Pro',
              color: '#6D757E',
            }}>
            <CreditCardIcon htmlColor="#6D757E" />
            Card on file:
          </Typography>
          <Typography
            component="p"
            sx={{
              font: '600 12px/14px Source Sans Pro',
              textAlign: 'end',
              color: hasCardOnFile ? '#28a745' : '#dc3545',
            }}>
            {hasCardOnFile ? 'Yes' : 'No'}
          </Typography>
        </Grid>
      )}
    </Grid>
    <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start">
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '5px',
          font: '600 12px/14px Source Sans Pro',
          color: '#6D757E',
        }}>
        <DateRangeIcon htmlColor="#6D757E" />
        Buckets:
      </Typography>
      <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
        {labBuckets
          ?.filter((b) => !b.startsWith('DME') && b !== 'NIGHT_OWL' && b !== 'NO_NEW')
          ?.map((bucket) => (
            <span key={bucket} className="badge bg-secondary" css={{ marginRight: 5 }}>
              {bucket}
            </span>
          ))}
      </Typography>
    </Grid>
  </Grid>
)

const ProviderInfo = ({
  officeId,
  officeName,
  companyName,
  physicianName,
  physicianNpi,
  testType,
  interpretingPhysician,
  followUpProviders,
  followUpCareNoneExplicitlySet,
  effectivePreferredDmeOfficeLabel,
  preferredDmeNoneExplicitlySet,
  physicianNotes,
  clientNote,
  order,
}: IProviderInfo) => (
  <Grid
    container
    direction="column"
    sx={{ background: 'white', padding: '10px', position: 'relative' }}
    justifyContent="flex-start">
    <Grid container wrap="nowrap" justifyContent="space-between" alignItems="center" mb={1}>
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '5px',
          font: '600 12px/14px Source Sans Pro',
          color: '#6D757E',
        }}>
        <BuildingIcon htmlColor="#6D757E" />
        DME Company:
      </Typography>
      <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
        {companyName || '-'}
      </Typography>
    </Grid>
    <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start" mb={1}>
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '5px',
          font: '600 12px/14px Source Sans Pro',
          color: '#6D757E',
        }}>
        <LocalShippingIcon htmlColor="#6D757E" />
        DME Office:
      </Typography>
      <div>
        <Can
          permission={UserPermission.EDIT_OFFICES}
          fallback={
            <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
              {officeName}
            </Typography>
          }>
          <Link to={`${routes.index}${routes.office.root}${routes.upsert}${officeId}`}>
            <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
              {officeName}
            </Typography>
          </Link>
        </Can>
      </div>
    </Grid>
    <Grid container wrap="nowrap" justifyContent="space-between" alignItems="center" mb={1}>
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '5px',
          font: '600 12px/14px Source Sans Pro',
          color: '#6D757E',
        }}>
        <LocalHospital htmlColor="#6D757E" />
        Physician:
      </Typography>
      <div>
        {physicianNpi ? (
          <Link
            to={`${routes.index}${routes.physician.root}${routes.physician.upsert}${physicianNpi}`}
            target="_blank"
            rel="noopener noreferrer">
            <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
              {physicianName}
            </Typography>
          </Link>
        ) : (
          <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
            {physicianName}
          </Typography>
        )}
      </div>
    </Grid>

    <Grid container wrap="nowrap" justifyContent="space-between" alignItems="center" mb={1}>
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '5px',
          font: '600 12px/14px Source Sans Pro',
          color: '#6D757E',
          flexShrink: 0,
          marginRight: '8px',
        }}>
        <NoteIcon htmlColor="#6D757E" />
        Physician Notes:
      </Typography>
      <div css={{ maxHeight: 40, overflowY: 'auto', minWidth: 0, textAlign: 'end' }}>
        {physicianNotes.length > 0 ? (
          physicianNotes.map((note) => (
            <Typography key={note.uuid} component="p" sx={{ font: '600 12px/14px Source Sans Pro' }}>
              {note.text}
            </Typography>
          ))
        ) : (
          <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro' }}>
            -
          </Typography>
        )}
      </div>
    </Grid>

    {testType === 'HST' && (
      <>
        <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '5px',
              font: '600 12px/14px Source Sans Pro',
              color: '#6D757E',
            }}>
            <LocalHospital htmlColor="#6D757E" />
            Interpreting Physician:
          </Typography>
          <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro', textAlign: 'end' }}>
            {interpretingPhysician || '-'}
          </Typography>
        </Grid>

        <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '5px',
              font: '600 12px/14px Source Sans Pro',
              color: '#6D757E',
              flexShrink: 0,
              marginRight: '8px',
            }}>
            <BuildingIcon htmlColor="#6D757E" />
            Following Up Provider:
          </Typography>
          <div
            css={{
              maxHeight: 110,
              overflowY: followUpProviders.length > 1 ? 'auto' : 'visible',
              minWidth: 0,
              textAlign: 'end',
            }}>
            {followUpProviders.length ? (
              followUpProviders.map((provider) => (
                <Typography key={provider.id} component="p" sx={{ font: '600 12px/14px Source Sans Pro' }}>
                  {provider.label}
                </Typography>
              ))
            ) : followUpCareNoneExplicitlySet ? (
              <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro' }}>
                None
              </Typography>
            ) : (
              <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro' }}>
                -
              </Typography>
            )}
          </div>
        </Grid>

        <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '5px',
              font: '600 12px/14px Source Sans Pro',
              color: '#6D757E',
              flexShrink: 0,
              marginRight: '8px',
            }}>
            <LocalShippingIcon htmlColor="#6D757E" />
            Preferred DME:
          </Typography>
          <div
            css={{
              maxHeight: 110,
              overflowY: followUpProviders.length > 1 ? 'auto' : 'visible',
              minWidth: 0,
              textAlign: 'end',
            }}>
            {effectivePreferredDmeOfficeLabel ? (
              <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro' }}>
                {effectivePreferredDmeOfficeLabel}
              </Typography>
            ) : preferredDmeNoneExplicitlySet ? (
              <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro' }}>
                None
              </Typography>
            ) : followUpProviders.length ? (
              followUpProviders.map((provider) => (
                <Typography key={provider.id} component="p" sx={{ font: '600 12px/14px Source Sans Pro' }}>
                  {provider.preferredDmeOfficeLabel || '-'}
                </Typography>
              ))
            ) : (
              <Typography component="p" sx={{ font: '600 12px/14px Source Sans Pro' }}>
                -
              </Typography>
            )}
          </div>
        </Grid>

        {clientNote && (
          <Grid
            container
            direction="column"
            mb={1}
            rowGap="5px"
            sx={{
              marginTop: '10px',
              background: '#FFF8E1',
              border: '1px solid #FFE082',
              borderRadius: '4px',
              padding: '5px',
            }}>
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '5px',
                font: '600 12px/14px Source Sans Pro',
                color: '#6D757E',
              }}>
              <NoteIcon htmlColor="#6D757E" />
              Client Note:
            </Typography>
            <div css={{ maxHeight: 100, overflowY: 'auto', minWidth: 0 }}>
              <Typography
                component="p"
                sx={{
                  font: '600 12px/14px Source Sans Pro',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}>
                {clientNote}
              </Typography>
            </div>
          </Grid>
        )}
      </>
    )}

    {testType === 'PSG' && (
      <>
        <TestConditionWidget order={order} />
        {order?.pinnedNote && <PinnedNoteDisplay pinnedNote={order.pinnedNote} />}
      </>
    )}
  </Grid>
)

const LocationInfo = ({
  deviceKind,
  serialNumber,
  deviceBrand,
  createMobileInvite,
  order,
  addNight,
  notes,
}: ILocationInfo) => {
  const [setNeededDeviceType] = useSetNeededDeviceTypeMutation()
  const [isUpdating, setIsUpdating] = useState(false)
  const [complaintModalOpen, setComplaintModalOpen] = useState(false)

  const handleDeviceChange = async (deviceType: NeededDeviceType | null) => {
    setIsUpdating(true)
    try {
      await setNeededDeviceType({
        orderId: Number(order.id),
        neededDeviceType: deviceType,
      }).unwrap()
    } catch (error) {
      console.error('Failed to update device type:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const mobileInviteCode = Object.values(order?.mobileInvites || {})
    ?.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1))
    ?.pop()?.code

  return (
    <Grid
      container
      direction="column"
      sx={{ background: 'white', padding: '5px 10px', position: 'relative' }}
      justifyContent="space-between">
      <Grid item container flexWrap="nowrap" alignItems="center" columnGap="5px">
        <IconButton sx={{ borderRadius: '100px', background: '#F4F6F9' }}>
          <CastIcon fontSize="small" htmlColor="#0FA2B4" />
        </IconButton>
        <Grid container direction="column">
          <Grid container flexWrap="nowrap" justifyContent="space-between">
            <Typography
              sx={{
                font: '600 12px/14px Source Sans Pro',
                color: '#6D757E',
                display: 'flex',
                alignItems: 'center',
                columnGap: '5px',
              }}>
              <DarkModeOutlinedIcon htmlColor="#6D757E" /> Device:
            </Typography>
            <Grid
              columnGap={'2px'}
              container
              flexWrap="nowrap"
              alignItems="center"
              justifyContent="flex-end">
              <Typography sx={{ font: '600 12px/14px Source Sans Pro' }}>{deviceKind}</Typography>
              {/* {order?.testType === 'HST' && (
              <IconButton
                onClick={onChangeDeviceClick}
                disableRipple
                sx={{ background: '#2C83BD', padding: '3px' }}>
                <AutorenewOutlinedIcon fontSize="small" htmlColor="#ffffff" />
              </IconButton>
            )} */}
            </Grid>
          </Grid>
          <Grid container flexWrap="nowrap" justifyContent="space-between">
            <Typography
              sx={{
                font: '600 12px/14px Source Sans Pro',
                color: '#6D757E',
                display: 'flex',
                alignItems: 'center',
                columnGap: '5px',
              }}>
              <TagIcon htmlColor="#6D757E" /> Serial No:
            </Typography>
            <Grid container width="auto" flexWrap="nowrap" alignItems="center" columnGap="5px">
              <Typography sx={{ font: '600 12px/14px Source Sans Pro' }}>{serialNumber}</Typography>
              {order?.device?.id && (
                <>
                  <Button
                    variant="outline-primary"
                    title="Report complaint"
                    onClick={() => setComplaintModalOpen(true)}
                    css={{ whiteSpace: 'nowrap', padding: '0 5px', fontSize: 12, lineHeight: '16px' }}>
                    Report
                  </Button>
                  <DeviceComplaintModal
                    open={complaintModalOpen}
                    onClose={() => setComplaintModalOpen(false)}
                    site="lab"
                    deviceId={Number(order.device.id)}
                    orderId={Number(order.id)}
                    deviceLabel={serialNumber}
                  />
                </>
              )}
            </Grid>
          </Grid>
          {deviceBrand && (
            <Grid container flexWrap="nowrap" justifyContent="space-between">
              <Typography
                sx={{
                  font: '600 12px/14px Source Sans Pro',
                  color: '#6D757E',
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '5px',
                }}>
                <SettingsApplicationsIcon htmlColor="#6D757E" /> Model:
              </Typography>
              <Typography sx={{ font: '600 12px/14px Source Sans Pro' }}>{deviceBrand}</Typography>
            </Grid>
          )}

          <Grid container flexWrap="nowrap" justifyContent="space-between">
            <Typography
              sx={{
                font: '600 12px/14px Source Sans Pro',
                color: '#6D757E',
                display: 'flex',
                alignItems: 'center',
                columnGap: '5px',
              }}>
              <CodeIcon htmlColor="#6D757E" /> Patient App Code:
            </Typography>
            {mobileInviteCode ? (
              <div className="d-flex align-items-center">
                <Button
                  className="mr-3"
                  variant="outline-primary"
                  disabled={!order?.patient?.email}
                  onClick={() =>
                    order &&
                    confirm('', {
                      title: 'Are you sure?',
                      description:
                        "This will re-generate the mobile invite code and send it to the patient's email.",
                      yesAction: 'Regenerate code',
                      noAction: 'Cancel',
                    })
                      .then(() => createMobileInvite())
                      .catch(() => {})
                  }
                  css={{ whiteSpace: 'nowrap', padding: '2px 5px' }}>
                  Generate new
                </Button>
                <Typography sx={{ font: '600 12px/14px Source Sans Pro' }}>
                  {mobileInviteCode}
                </Typography>
              </div>
            ) : order?.testType === 'HST' ? (
              <Button
                variant="outline-primary"
                disabled={!order?.patient?.email}
                onClick={() => order && createMobileInvite()}
                css={{ whiteSpace: 'nowrap', padding: '2px 5px' }}>
                Create
              </Button>
            ) : (
              '-'
            )}
          </Grid>

          {order?.testType === 'HST' && (
            <Grid container flexWrap="nowrap" marginTop="5px" justifyContent="space-between">
              <Typography
                sx={{
                  font: '600 12px/14px Source Sans Pro',
                  color: '#6D757E',
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '5px',
                }}>
                <BedIcon htmlColor="#6D757E" /> Nights:
              </Typography>
              <div className="d-flex align-items-center">
                <Typography sx={{ font: '600 12px/14px Source Sans Pro' }}>
                  {order?.deviceData?.filter((x) => x?.params?.isValid)?.length || '-'} /{' '}
                  {order?.tests?.length || '-'}
                </Typography>
                <Button
                  className="ml-2"
                  variant="outline-primary"
                  onClick={() =>
                    order &&
                    confirm('', {
                      title: 'Are you sure?',
                      description: 'This will add additional night for the order.',
                      yesAction: 'Yes',
                      noAction: 'Cancel',
                    })
                      .then(() =>
                        addNight(
                          ([...(order?.tests || [])]?.sort((a, b) => b?.night - a?.night)?.[0]
                            ?.night || 0) + 1,
                        ),
                      )
                      .catch((e) => {
                        console.log(e)
                      })
                  }
                  css={{ whiteSpace: 'nowrap', padding: '0 5px' }}>
                  + Add night
                </Button>
              </div>
            </Grid>
          )}

          {order?.testType === 'HST' && (
            <Grid container flexWrap="nowrap" justifyContent="space-between" alignItems="center" sx={{ marginTop: '6px' }}>
              <Typography
                sx={{
                  font: '600 12px/14px Source Sans Pro',
                  color: '#6D757E',
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '5px',
                }}>
                <DevicesIcon htmlColor="#6D757E" /> Device Type Needed:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={order?.neededDeviceType || ''}
                  onChange={(e) =>
                    handleDeviceChange((e.target.value || null) as NeededDeviceType | null)
                  }
                  disabled={isUpdating}
                  displayEmpty
                  sx={{
                    font: '600 12px/14px Source Sans Pro',
                    height: '24px',
                  }}>
                  <MenuItem value="">
                    <em>Select Device</em>
                  </MenuItem>
                  {NEEDED_DEVICE_TYPES.map((deviceType) => (
                    <MenuItem key={deviceType} value={deviceType}>
                      {deviceType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Grid>
      {order?.testType !== 'PSG' && (
        <div
          css={{
            marginTop: 12,
            '& #testConditions': {
              paddingRight: 0,
            },
          }}>
          <TestConditionWidget order={order} />
        </div>
      )}
      {notes && <PinnedNoteDisplay pinnedNote={notes} />}
    </Grid>
  )
}

export const PatientHeaderTwo = ({
  order,
  onChangeDeviceClick,
  createMobileInvite,
  addNight,
  companyName,
}: PatientHeaderTwoProps) => {
  const currency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })
  const referringPhysicianNpi = order?.physician?.npi ?? order?.verifiedPhysician?.npi
  const { data: physicianNotesData = [] } = useGetLabPhysicianNotesQuery(
    { npi: referringPhysicianNpi || '' },
    { skip: !referringPhysicianNpi },
  )
  const { data: effectiveFollowUp } = useGetEffectiveFollowUpPhysiciansQuery(
    {
      physicianNpi: referringPhysicianNpi || '',
      officeId: order?.officeId != null ? Number(order.officeId) : undefined,
    },
    {
      skip: !referringPhysicianNpi || order?.testType !== 'HST',
    },
  )
  const { data: effectivePreferredDme } = useGetEffectivePreferredDmeOfficeQuery(
    {
      physicianNpi: referringPhysicianNpi || '',
      officeId: order?.officeId != null ? Number(order.officeId) : undefined,
    },
    {
      skip: !referringPhysicianNpi || order?.testType !== 'HST',
    },
  )
  const effectivePreferredDmeOfficeId = effectivePreferredDme?.effectivePreferredDmeOfficeId
  const { data: effectivePreferredDmeOffice } = useGetLabOfficeQuery(
    { id: Number(effectivePreferredDmeOfficeId) },
    { skip: effectivePreferredDmeOfficeId == null },
  )
  const { data: orderOffice } = useGetLabOfficeQuery(
    { id: Number(order?.officeId) },
    { skip: order?.officeId == null || order?.testType !== 'HST' },
  )
  const effectivePreferredDmeOfficeLabel = useMemo(() => {
    if (!effectivePreferredDmeOffice?.officeName) {
      return null
    }

    const location = [effectivePreferredDmeOffice.addressCity, effectivePreferredDmeOffice.addressState]
      .filter(Boolean)
      .join(', ')

    return [effectivePreferredDmeOffice.officeName, location ? `(${location})` : null]
      .filter(Boolean)
      .join(' ')
  }, [effectivePreferredDmeOffice])
  const latestInterpretingPhysician = useMemo(
    () =>
      [...(order?.reportRenderRequests || [])]
        .reverse()
        .find((request) => request.type === 'REPORT_PDF' && request.interpretingPhysician)
        ?.interpretingPhysician,
    [order?.reportRenderRequests],
  )
  const followUpProviderItems = useMemo(
    () =>
      (effectiveFollowUp?.items ?? []).map((item) => {
        const physician = item.followUpPhysician
        const label = isOrganization(physician?.providerType)
          ? physician?.organizationName || item.followUpPhysicianNpi
          : physicianFullName(physician?.firstName, physician?.lastName, physician?.credential) ||
            item.followUpPhysicianNpi

        return {
          id: String(physician?.id || item.followUpPhysicianNpi),
          label,
          preferredDmeOfficeLabel: item.preferredDmeOfficeLabel,
        }
      }),
    [effectiveFollowUp?.items],
  )

  const address =
    !order?.patient?.deliverySameAsPrimary && order?.patient?.deliveryAddressStreetLine1
      ? {
          addressLine1: order?.patient?.deliveryAddressStreetLine1 || '-',
          addressLine2: order?.patient?.deliveryAddressStreetLine2,
          city: order?.patient?.deliveryAddressCity || '-',
          state: order?.patient?.deliveryAddressState || '-',
          zip: order?.patient?.deliveryAddressZip?.replace(/_/g, '') || '-',
        }
      : {
          addressLine1: order?.patient?.primaryAddressStreetLine1 || '-',
          addressLine2: order?.patient?.primaryAddressStreetLine2,
          city: order?.patient?.primaryAddressCity || '-',
          state: order?.patient?.primaryAddressState || '-',
          zip: order?.patient?.primaryAddressZip?.replace(/_/g, '') || '-',
        }

  const patient = {
    patientInfo: {
      id: order?.patient?.id,
      name: order?.patient?.firstName
        ? [fullName(order?.patient?.firstName, order?.patient?.lastName), normalizeSuffix(order?.patient?.suffix)].filter(Boolean).join(' ')
        : '-',
      testTitle: order
        ? `${testName[order?.testType as keyof typeof testName]} #${order?.id}`
        : '-',
      dateOfBirth: order?.patient?.dob || '-',
      email: order?.patient?.email || '-',
      homePhone: order?.patient?.homePhone || '-',
      smartPhone: order?.patient?.mobilePhone || '-',
      voiceOptOut: order?.patient?.voiceOptOut,
      ...address,
      rxTestType:
        order?.rxRecognitionData?.[0]?.data?.data?.testType ||
        order?.rxRecognitionData?.[0]?.data?.data?.organization_custom_fields?.ordered_service,
      testTypeMatch:
        order?.rxRecognitionData?.[0]?.verificationResult?.rx?.data?.testTypeMatches === false
          ? false
          : order?.testType !== 'POX' ||
            !order?.rxRecognitionData?.[0]?.data?.data?.organization_custom_fields
              ?.ordered_service ||
            (order?.testType === 'POX' &&
              order?.rxRecognitionData?.[0]?.data?.data?.organization_custom_fields?.ordered_service
                ?.toLowerCase()
                ?.includes('oximetry')),
    },
    contactInfo: {
      patientId: order?.patient?.id,
      // A card saved through Receive Prepayment lands on the patient, not on the
      // order's payment session — either one means there is a card to charge.
      stripeCustomerId: order?.hst?.paymentSession?.customer || order?.patient?.stripeCustomerId,
      hasCardOnFile:
        order?.testType === 'HST'
          ? (order?.hst?.paymentSession?.status === 'complete' &&
              Boolean(order?.hst?.paymentSession?.customer)) ||
            Boolean(order?.patient?.stripeCustomerId)
          : undefined,
      coPayAmount: order?.copay || order?.copay === 0 ? currency.format(order.copay) : '-',
      payor:
        order?.payers?.[0]?.labPayer?.name +
          `${order?.payers?.[1]?.labPayer?.name ? ` / ${order?.payers?.[1]?.labPayer?.name}` : ''}` ||
        '-',
      effectiveDate: order?.payers?.[0]?.effectiveDate,
      terminationDate: order?.payers?.[0]?.terminationDate,
      authValid: order?.payersInfo?.authorizationNumberDenied
        ? 'DENIED'
        : order?.payersInfo?.authorizationValidStartDate
          ? `${moment(order?.payersInfo?.authorizationValidStartDate).format('MM/DD/YYYY')} - ${moment(
              order?.payersInfo?.authorizationValidEndDate,
            ).format('MM/DD/YYYY')}`
          : '-',
      units: order?.payersInfo?.maxUnitsBilledOut != null ? String(order.payersInfo.maxUnitsBilledOut) : '-',
      labBuckets: order?.labBuckets,
    },
    providerInfo: {
      officeName: `${order?.office?.officeName} - ${order?.office?.addressCity}, ${order?.office?.addressState}`,
      officeId: order?.officeId,
      companyName,
      physicianName: physicianFullName(
        (order?.physician || order?.verifiedPhysician)?.firstName,
        (order?.physician || order?.verifiedPhysician)?.lastName,
        (order?.physician || order?.verifiedPhysician)?.credential,
      ),
      physicianNpi: order?.physician?.npi ?? order?.verifiedPhysician?.npi,
      testType: order?.testType,
      interpretingPhysician: latestInterpretingPhysician,
      followUpProviders: followUpProviderItems,
      followUpCareNoneExplicitlySet: effectiveFollowUp?.noneExplicitlySet ?? false,
      effectivePreferredDmeOfficeLabel,
      preferredDmeNoneExplicitlySet: effectivePreferredDme?.noneExplicitlySet ?? false,
      physicianNotes: physicianNotesData,
      clientNote: orderOffice?.clientNote,
      order,
    },
    locationInfo: {
      deviceKind: order?.thirdPartyDevice
        ? deviceTypes[order?.thirdPartyDevice?.deviceType as keyof typeof deviceTypes]
        : order?.device
          ? order?.device?.brand
          : '-',
      serialNumber: order?.thirdPartyDevice?.nightOwlSerial || order?.device?.serial || '-',
      deviceBrand: order?.device?.brand,
      notes: order?.pinnedNote,
    },
  }

  return (
    <Grid
      container
      columnGap={{ xs: 0, md: '15px' }}
      rowGap={{ xs: '10px', md: 0 }}
      flexWrap={{ xs: 'wrap', md: 'nowrap' }}
      direction={{ xs: 'column', md: 'row' }}>
      <PatientInfo
        {...patient.patientInfo}
        onChangeDeviceClick={onChangeDeviceClick}
        order={order}
      />
      <ContactInfo {...patient.contactInfo} testType={order?.testType} />
      {order?.testType !== 'PSG' && (
        <LocationInfo
          {...patient.locationInfo}
          order={order}
          addNight={(night) => addNight(night)}
          createMobileInvite={() =>
            createMobileInvite({
              orderId: Number(order?.id),
              method: 'email',
              email: order?.patient?.email,
            })
          }
        />
      )}
      <ProviderInfo {...patient.providerInfo} />
    </Grid>
  )
}
